import React from 'react'
import './index.css'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { store } from './store'
import { router } from './routes'
import { ErrorBoundary } from './components/ErrorBoundary'
import { APIClient } from './services/apiClient'
import { ToastContainer } from './components/core/Feedback/Toast'
import { initializeAriaLiveRegions } from './utils/ariaLiveRegion'
import { staleTimeConfig } from './hooks/usePrefetch'
import { ThemeProviderWrapper } from './components/ThemeProviderWrapper'
import { RTLProvider } from './components/RTLProvider'
import i18n from './i18n'; // Initialize i18n
import './services/firebase'; // Initialize Firebase

import { defaultSessionAdapter } from './transport'

// Initialize API client
APIClient.initialize()

// Initialize ARIA live regions for screen reader announcements
initializeAriaLiveRegions()

// Create React Query client with optimized caching configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: staleTimeConfig.dashboard, // Default 30 seconds
      gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Retry failed requests once
    },
  },
})

// Coordinate SessionPort with React Query and Router
defaultSessionAdapter.onSessionCleared(() => {
  queryClient.clear()
})
defaultSessionAdapter.setNavigator((path) => {
  router.navigate(path)
})

// Global unhandled rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  if (window.__errorTracker) {
    window.__errorTracker({
      message: 'Unhandled Promise Rejection',
      reason: event.reason,
      timestamp: new Date().toISOString()
    })
  }
})

// Global error handler for runtime errors
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error)
  if (window.__errorTracker) {
    window.__errorTracker({
      message: event.message,
      error: event.error,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString()
    })
  }
})

// Safely render the app with error recovery
const renderApp = () => {
  try {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <ErrorBoundary>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <RTLProvider>
                <ThemeProviderWrapper>
                  <RouterProvider router={router} />
                  <ToastContainer />
                </ThemeProviderWrapper>
              </RTLProvider>
            </QueryClientProvider>
          </Provider>
        </ErrorBoundary>
      </React.StrictMode>,
    )
  } catch (error) {
    console.error('Fatal error during app initialization:', error)
    // Show a basic error message if React fails to render
    const root = document.getElementById('root')
    if (root) {
      root.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; text-align: center; font-family: system-ui, -apple-system, sans-serif;">
          <h1 style="color: #d32f2f; margin-bottom: 16px;">${i18n.t('errors.appError')}</h1>
          <p style="color: #666; margin-bottom: 24px;">${i18n.t('errors.appErrorDesc')}</p>
          <button onclick="window.location.reload()" style="padding: 12px 24px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 16px;">
            ${i18n.t('errors.reloadPage')}
          </button>
          ${import.meta.env.DEV ? `<pre style="margin-top: 24px; padding: 16px; background: #f5f5f5; border-radius: 4px; text-align: left; overflow: auto; max-width: 600px;">${error}</pre>` : ''}
        </div>
      `
    }
  }
}

renderApp()